package com.eladfinish.bible.contest.data.common

import com.russhwolf.settings.ObservableSettings
import com.russhwolf.settings.Settings
import com.russhwolf.settings.SettingsListener

/**
 * multiplatform-settings' StorageSettings (the browser localStorage-backed implementation used
 * on Kotlin/JS) doesn't implement ObservableSettings — there's no cross-tab or same-tab change
 * event to hook into for same-window writes (see russhwolf/multiplatform-settings#155, still open
 * upstream). This wraps it and notifies listeners manually whenever *this instance* writes a key,
 * which covers every write the app makes (all through the injected Settings/ObservableSettings) —
 * it just won't see changes from other means (e.g. another browser tab).
 */
class WebObservableSettings(private val delegate: Settings) : ObservableSettings, Settings by delegate {

    private val listenersByKey = mutableMapOf<String, MutableSet<() -> Unit>>()

    private fun notify(key: String) {
        listenersByKey[key]?.toList()?.forEach { it() }
    }

    private fun addListener(key: String, notifyNow: () -> Unit): SettingsListener {
        notifyNow()
        val listeners = listenersByKey.getOrPut(key) { mutableSetOf() }
        listeners += notifyNow
        return object : SettingsListener {
            override fun deactivate() {
                listeners -= notifyNow
            }
        }
    }

    override fun putInt(key: String, value: Int) {
        delegate.putInt(key, value); notify(key)
    }

    override fun putLong(key: String, value: Long) {
        delegate.putLong(key, value); notify(key)
    }

    override fun putString(key: String, value: String) {
        delegate.putString(key, value); notify(key)
    }

    override fun putFloat(key: String, value: Float) {
        delegate.putFloat(key, value); notify(key)
    }

    override fun putDouble(key: String, value: Double) {
        delegate.putDouble(key, value); notify(key)
    }

    override fun putBoolean(key: String, value: Boolean) {
        delegate.putBoolean(key, value); notify(key)
    }

    override fun remove(key: String) {
        delegate.remove(key); notify(key)
    }

    override fun clear() {
        delegate.clear()
        listenersByKey.keys.toList().forEach { notify(it) }
    }

    override fun addIntListener(key: String, defaultValue: Int, callback: (Int) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getInt(key, defaultValue)) }

    override fun addLongListener(key: String, defaultValue: Long, callback: (Long) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getLong(key, defaultValue)) }

    override fun addStringListener(key: String, defaultValue: String, callback: (String) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getString(key, defaultValue)) }

    override fun addFloatListener(key: String, defaultValue: Float, callback: (Float) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getFloat(key, defaultValue)) }

    override fun addDoubleListener(key: String, defaultValue: Double, callback: (Double) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getDouble(key, defaultValue)) }

    override fun addBooleanListener(key: String, defaultValue: Boolean, callback: (Boolean) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getBoolean(key, defaultValue)) }

    override fun addIntOrNullListener(key: String, callback: (Int?) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getIntOrNull(key)) }

    override fun addLongOrNullListener(key: String, callback: (Long?) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getLongOrNull(key)) }

    override fun addStringOrNullListener(key: String, callback: (String?) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getStringOrNull(key)) }

    override fun addFloatOrNullListener(key: String, callback: (Float?) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getFloatOrNull(key)) }

    override fun addDoubleOrNullListener(key: String, callback: (Double?) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getDoubleOrNull(key)) }

    override fun addBooleanOrNullListener(key: String, callback: (Boolean?) -> Unit): SettingsListener =
        addListener(key) { callback(delegate.getBooleanOrNull(key)) }
}
